// Mock license count for the design fixture only. Real LS license capacity is per-customer billing (RECE-1408).
export const RAB_LICENSES = 7;

// V1 engine modes are DOM + Rent only. GEO (region-scoped optimization) is deferred to V1.5/V2;
// when it returns it brings its own region selector as an engine parameter. The Scope control here
// is a pure view filter (narrows displayed rows), not an engine input.
export type OptimizerId = 'dom' | 'rent';

export type OptimizableListing = {
  id: string;
  daysOnMarket: number;
  market: string;
  rent: number;
};

export type OptimizerDef = {
  id: OptimizerId;
  label: string;
  description: string;
  rank: <T extends OptimizableListing>(listings: Array<T>) => Array<T>;
};

export const OPTIMIZERS: Record<OptimizerId, OptimizerDef> = {
  dom: {
    id: 'dom',
    label: 'Days on market',
    description: 'Prioritize vacancies that have been listed the longest.',
    rank: (listings) => [...listings].sort((a, b) => b.daysOnMarket - a.daysOnMarket),
  },
  rent: {
    id: 'rent',
    label: 'Rent',
    description: 'Prioritize the highest rent vacancies.',
    rank: (listings) => [...listings].sort((a, b) => b.rent - a.rent),
  },
};

export const OPTIMIZER_LIST = Object.values(OPTIMIZERS);

export type DeploymentInput<T extends OptimizableListing> = {
  listings: Array<T>;
  optimizer: OptimizerId;
  pinnedIds: Set<string>;
  excludeIds?: Set<string>;
  isVacant: (listing: T) => boolean;
  licenses?: number;
};

export type DeploymentResult<T extends OptimizableListing = OptimizableListing> = {
  autoIds: Set<string>;
  idle: number;
  ranked: Array<T>;
  uncovered: number;
};

export function computeRabDeployment<T extends OptimizableListing>(input: DeploymentInput<T>): DeploymentResult<T> {
  const { listings, optimizer, pinnedIds, excludeIds, isVacant, licenses = RAB_LICENSES } = input;
  const exclude = excludeIds ?? new Set<string>();
  const def = OPTIMIZERS[optimizer];
  const available = Math.max(0, licenses - pinnedIds.size);
  const eligible = listings.filter(
    (listing) => isVacant(listing) && !pinnedIds.has(listing.id) && !exclude.has(listing.id),
  );
  const ranked = def.rank(eligible);
  const autoIds = new Set(ranked.slice(0, available).map((listing) => listing.id));

  return {
    autoIds,
    idle: Math.max(0, available - autoIds.size),
    ranked,
    uncovered: Math.max(0, ranked.length - available),
  };
}
